import { ReactNode } from 'react';
import styled from '@emotion/styled';
import { useAtomValue } from 'jotai';
import { adminTablesAtom, adminWorkspaceAtom } from '@jotai/admin/atoms';
import { OnboardingColor } from '@resources/colors';
import { colFlex, rowFlex } from '@styles/flexStyles';
import { isOnboardingStepCompleted } from '@utils/onboarding';
import { ONBOARDING_MIN_TABLE_COUNT, ONBOARDING_STEP, OnboardingStep } from './onboardingData';

interface StepHintCopy {
  title: string;
  description: string;
}

const STEP_HINT_COPY: Partial<Record<OnboardingStep, StepHintCopy>> = {
  [ONBOARDING_STEP.INFO]: {
    title: '주점 기본 정보를 입력해주세요',
    description: '주점명, 대표 사진, 주점 설명을 모두 등록한 뒤 ‘편집 완료’ 버튼을 눌러주세요.',
  },
  [ONBOARDING_STEP.TABLES]: {
    title: '테이블 설정과 배치를 완료해주세요',
    description: '테이블을 2개 이상 추가한 뒤 테이블을 1개 이상 배치하고 저장해야 온보딩을 완료할 수 있습니다.',
  },
  [ONBOARDING_STEP.MENU]: {
    title: '상품을 1개 이상 등록해주세요',
    description: '카테고리를 정리한 뒤 상품을 1개 이상 등록하면 온보딩을 완료할 수 있습니다.',
  },
};

const Banner = styled.div<{ width: string }>`
  width: ${({ width }) => width};
  padding: 14px 18px;
  margin-bottom: 12px;
  border: 1px solid ${OnboardingColor.STEP_ACTIVE_BORDER};
  background: ${OnboardingColor.STEP_PENDING_BG};
  border-radius: 10px;
  gap: 16px;
  box-sizing: border-box;
  ${rowFlex({ justify: 'space-between', align: 'center' })}
`;

const TextColumn = styled.div`
  flex: 1;
  min-width: 0;
  gap: 4px;
  ${colFlex({ align: 'flex-start' })}
`;

const ActionSlot = styled.div`
  flex-shrink: 0;
`;

const Title = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${OnboardingColor.TITLE_TEXT};
`;

const Description = styled.span`
  font-size: 13px;
  color: ${OnboardingColor.BODY_TEXT};
`;

interface OnboardingStepHintProps {
  step: OnboardingStep;
  width?: string;
  action?: ReactNode;
}

function OnboardingStepHint({ step, width = '100%', action }: OnboardingStepHintProps) {
  const workspace = useAtomValue(adminWorkspaceAtom);
  const tables = useAtomValue(adminTablesAtom);
  const copy =
    step === ONBOARDING_STEP.TABLES && workspace.tableCount >= ONBOARDING_MIN_TABLE_COUNT
      ? {
          title: '테이블을 1개 이상 배치해주세요',
          description: '상단에서 배치 보기를 선택한 뒤 ‘배치 편집’에서 테이블을 1개 이상 배치하고 저장해주세요.',
        }
      : STEP_HINT_COPY[step];

  if (!copy) return null;
  if (!workspace.isOnboarding) return null;
  if (isOnboardingStepCompleted(workspace, step, tables)) return null;

  return (
    <Banner width={width}>
      <TextColumn>
        <Title>{copy.title}</Title>
        <Description>{copy.description}</Description>
      </TextColumn>
      {action && <ActionSlot>{action}</ActionSlot>}
    </Banner>
  );
}

export default OnboardingStepHint;
